import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock, Ship, Plane, Truck } from 'lucide-react';
import Breadcrumb from './Breadcrumb';
import './ServicePageTemplate.css';

const ServicePageTemplate = ({
  heroTitle,
  heroImage,
  introText,
  services,
  serviceIcons,
  benefits,
  benefitIcons,
  callout,
  slug,
  faq,
  relatedServices,
}) => {
  const [formData, setFormData] = useState({
    name: '', company: '', email: '', phone: '',
    origin: '', destination: '', commodity: '',
    date: '', weight: '', message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [openFaq, setOpenFaq] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    // Clear error on change
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: '' });
    }
  };

  // Blur validation
  const handleBlur = (e) => {
    const { name, value, required, type } = e.target;
    const newErrors = { ...errors };
    if (required && !value.trim()) {
      newErrors[name] = 'This field is required.';
    } else if (type === 'email' && value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      newErrors[name] = 'Enter a valid email address.';
    } else {
      delete newErrors[name];
    }
    setErrors(newErrors);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Validate all required fields
    const required = ['name', 'company', 'email', 'phone', 'origin', 'destination', 'commodity'];
    const newErrors = {};
    required.forEach(field => {
      if (!formData[field].trim()) newErrors[field] = 'This field is required.';
    });
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Enter a valid email address.';
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      // Focus first invalid field
      const firstErr = required.find(f => newErrors[f]);
      if (firstErr) document.getElementById(`svc-${firstErr}-${slug}`)?.focus();
      return;
    }
    setSubmitting(true);
    // Simulate submit (replace with actual POST)
    setTimeout(() => {
      setSubmitting(false);
      setSubmitted(true);
    }, 800);
  };

  const breadcrumbItems = [
    { label: 'Home', path: '/' },
    { label: 'Services', path: '/services/sea-freight' },
    { label: heroTitle, path: '#' },
  ];

  // Default related services if not provided
  const defaultRelated = [
    { icon: Ship, title: 'Sea Freight', desc: 'Reliable ocean freight solutions worldwide.', link: '/services/sea-freight' },
    { icon: Plane, title: 'Air Freight', desc: 'Fast, time-critical air cargo solutions.', link: '/services/air-freight' },
    { icon: Truck, title: 'Land Freight', desc: 'Domestic and cross-border road transport.', link: '/services/land-freight' },
  ].filter(s => !s.title.toLowerCase().includes(heroTitle.toLowerCase().split(' ')[0]));

  const related = relatedServices || defaultRelated.slice(0, 3);

  return (
    <div className="svc-page">
      {/* ─── Split Hero with photo slot ─── */}
      <section className="svc-hero">
        <div className="svc-hero__text">
          <div className="svc-hero__text-inner">
            <Breadcrumb items={breadcrumbItems} />
            <h1 className="svc-hero__title">{heroTitle.toUpperCase()}</h1>
            <div className="svc-hero__rule" />
            {introText.map((text, i) => (
              <p key={i} className="svc-hero__desc">{text}</p>
            ))}
          </div>
        </div>
        <div className="svc-hero__media" aria-hidden="true">
          {/* Background image */}
          {heroImage && (
            <img
              src={heroImage}
              alt=""
              className="svc-hero__media-img"
              width="1600"
              height="1067"
              fetchpriority="high"
              decoding="async"
            />
          )}
          {/* Overlay gradient */}
          <div className="svc-hero__media-gradient" />
          {/* Animated elements layered on top */}
          <div className="svc-hero__media-anim">
            {/* Floating triangles matching brand mark */}
            <div className="svc-hero__float svc-hero__float--1" />
            <div className="svc-hero__float svc-hero__float--2" />
            <div className="svc-hero__float svc-hero__float--3" />
            <div className="svc-hero__float svc-hero__float--4" />
            <div className="svc-hero__float svc-hero__float--5" />
            <div className="svc-hero__float svc-hero__float--6" />
            {/* Glowing orb */}
            <div className="svc-hero__glow" />
            {/* Animated route line */}
            <svg className="svc-hero__route-svg" viewBox="0 0 600 500" preserveAspectRatio="none">
              <path d="M-10,350 C100,280 200,320 300,250 S450,180 600,220" fill="none" stroke="rgba(20,184,166,0.2)" strokeWidth="2" strokeDasharray="10 6" className="svc-hero__route-anim" />
              <path d="M-10,400 C120,330 220,370 350,300 S500,230 610,270" fill="none" stroke="rgba(20,184,166,0.1)" strokeWidth="1.5" strokeDasharray="8 5" className="svc-hero__route-anim svc-hero__route-anim--2" />
              <circle r="3.5" fill="var(--teal-light)" opacity="0.9" filter="url(#svc-glow)">
                <animateMotion dur="6s" repeatCount="indefinite" path="M-10,350 C100,280 200,320 300,250 S450,180 600,220" />
              </circle>
              <defs>
                <filter id="svc-glow">
                  <feGaussianBlur stdDeviation="3" />
                </filter>
              </defs>
            </svg>
            {/* Grid perspective */}
            <div className="svc-hero__grid" />
          </div>
        </div>
      </section>

      {/* ─── Body: 2-column — content left, form right ─── */}
      <section className="svc-body">
        <div className="container svc-body__grid">
          {/* ── Left column ── */}
          <div className="svc-body__main">
            {/* Service list — 3 columns, column-major, de-carded */}
            <div className="svc-services">
              <h2 className="svc-services__heading">
                OUR {heroTitle.toUpperCase()} SERVICES
              </h2>
              <div className="svc-services__rule" />
              <div className="svc-services__list">
                {services.map((service, i) => {
                  const IconComponent = serviceIcons && serviceIcons[i] ? serviceIcons[i] : null;
                  return (
                    <div key={i} className="svc-services__item">
                      {IconComponent ? (
                        <IconComponent size={22} className="svc-services__icon" aria-hidden="true" />
                      ) : (
                        <span className="svc-services__icon-dot" aria-hidden="true" />
                      )}
                      <span className="svc-services__label">{service}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Callout strip */}
            <div className="svc-callout">
              <div className="svc-callout__content">
                <Clock size={20} className="svc-callout__icon" aria-hidden="true" />
                <div className="svc-callout__text">
                  <p className="svc-callout__eyebrow">
                    {callout?.eyebrow || `${heroTitle.toUpperCase()} YOU CAN RELY ON.`}
                  </p>
                  <p className="svc-callout__sub">
                    {callout?.sub || 'Our experts are here to help.'}
                  </p>
                </div>
              </div>
              <a href="#quote-form" className="btn btn-navy btn-sm svc-callout__btn">
                {callout?.ctaLabel || `TALK TO OUR TEAM`} <ArrowRight size={14} />
              </a>
            </div>

            {/* Why Choose Denver Logistics — horizontal strip in left column */}
            {benefits && benefits.length > 0 && (
              <div className="svc-why-section">
                <h3 className="svc-why-section__heading">WHY CHOOSE DENVER LOGISTICS?</h3>
                <div className="svc-why-section__rule" />
                <div className="svc-why-section__grid">
                  {benefits.map((b, i) => {
                    const BenefitIcon = benefitIcons && benefitIcons[i] ? benefitIcons[i] : null;
                    return (
                      <div key={i} className="svc-why-card">
                        <div className="svc-why-card__icon">
                          {BenefitIcon ? (
                            <BenefitIcon size={22} strokeWidth={1.5} aria-hidden="true" />
                          ) : (
                            <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
                              <circle cx="11" cy="11" r="11" fill="rgba(13,148,136,0.1)" />
                              <path d="M7 11l3 3 5-5" stroke="var(--teal)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          )}
                        </div>
                        <h4 className="svc-why-card__title">{b.title}</h4>
                        <p className="svc-why-card__desc">{b.description}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* FAQ Accordion */}
            {faq && faq.length > 0 && (
              <div className="svc-faq">
                <h3 className="svc-faq__heading">FREQUENTLY ASKED QUESTIONS</h3>
                <div className="svc-faq__rule" />
                <div className="svc-faq__list">
                  {faq.map((item, i) => (
                    <div key={i} className={`svc-faq__item ${openFaq === i ? 'svc-faq__item--open' : ''}`}>
                      <button
                        className="svc-faq__question"
                        onClick={() => setOpenFaq(openFaq === i ? null : i)}
                        aria-expanded={openFaq === i}
                      >
                        <span>{item.q}</span>
                        <svg
                          width="18" height="18" viewBox="0 0 18 18" fill="none"
                          className="svc-faq__chevron" aria-hidden="true"
                        >
                          <path d="M4.5 6.75L9 11.25L13.5 6.75" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                      <div className="svc-faq__answer">
                        <p>{item.a}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Related Services */}
            <div className="svc-related">
              <h3 className="svc-related__heading">RELATED SERVICES</h3>
              <div className="svc-related__rule" />
              <div className="svc-related__grid">
                {related.map((s, i) => (
                  <Link to={s.link} key={i} className="svc-related__card">
                    <div className="svc-related__card-icon">
                      {s.icon && <s.icon size={24} aria-hidden="true" />}
                    </div>
                    <h4 className="svc-related__card-title">{s.title}</h4>
                    <p className="svc-related__card-desc">{s.desc}</p>
                    <span className="svc-related__card-link">
                      Explore <ArrowRight size={14} />
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* ── Right rail — form only ── */}
          <aside className="svc-body__rail">
            <div className="svc-quote" id="quote-form">
              <h3 className="svc-quote__heading">REQUEST A QUOTE</h3>
              {submitted ? (
                <div className="svc-quote__success" role="status" tabIndex="-1" ref={el => el && el.focus()}>
                  <svg width="48" height="48" viewBox="0 0 48 48" fill="none" aria-hidden="true">
                    <circle cx="24" cy="24" r="24" fill="rgba(13,148,136,0.1)" />
                    <path d="M14 24l7 7 13-13" stroke="var(--teal)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <h4>Thank you!</h4>
                  <p>We'll reply within 4 business hours.</p>
                  <button
                    onClick={() => { setSubmitted(false); setFormData({ name:'',company:'',email:'',phone:'',origin:'',destination:'',commodity:'',date:'',weight:'',message:'' }); }}
                    className="btn btn-outline btn-sm"
                    style={{ width: '100%', justifyContent: 'center', marginTop: '12px' }}
                  >
                    Submit another
                  </button>
                </div>
              ) : (
                <form className="svc-quote__form" onSubmit={handleSubmit} noValidate>
                  <input type="hidden" name="service" value={slug || heroTitle} />
                  {/* Honeypot — fully hidden from assistive tech */}
                  <div style={{ position: 'absolute', left: '-9999px' }} aria-hidden="true">
                    <input type="text" name="website" tabIndex="-1" autoComplete="off" aria-hidden="true" />
                  </div>

                  <p className="svc-quote__required-note">* Required fields</p>
                  <div aria-live="polite" className="visually-hidden" id={`form-status-${slug}`}></div>

                  <div className="svc-quote__row">
                    <div className={`svc-quote__field ${errors.name ? 'svc-quote__field--error' : ''}`}>
                      <label htmlFor={`svc-name-${slug}`}>Name *</label>
                      <input type="text" id={`svc-name-${slug}`} name="name" required value={formData.name} onChange={handleChange} onBlur={handleBlur} placeholder="Your name" aria-required="true" aria-invalid={!!errors.name} aria-describedby={errors.name ? `err-name-${slug}` : undefined} autoComplete="name" />
                      {errors.name && <p className="svc-quote__error" id={`err-name-${slug}`}>{errors.name}</p>}
                    </div>
                    <div className={`svc-quote__field ${errors.company ? 'svc-quote__field--error' : ''}`}>
                      <label htmlFor={`svc-company-${slug}`}>Company *</label>
                      <input type="text" id={`svc-company-${slug}`} name="company" required value={formData.company} onChange={handleChange} onBlur={handleBlur} placeholder="Company name" aria-required="true" aria-invalid={!!errors.company} aria-describedby={errors.company ? `err-company-${slug}` : undefined} autoComplete="organization" />
                      {errors.company && <p className="svc-quote__error" id={`err-company-${slug}`}>{errors.company}</p>}
                    </div>
                  </div>

                  <div className="svc-quote__row">
                    <div className={`svc-quote__field ${errors.email ? 'svc-quote__field--error' : ''}`}>
                      <label htmlFor={`svc-email-${slug}`}>Email *</label>
                      <input type="email" id={`svc-email-${slug}`} name="email" required value={formData.email} onChange={handleChange} onBlur={handleBlur} placeholder="your@email.com" aria-required="true" aria-invalid={!!errors.email} aria-describedby={errors.email ? `err-email-${slug}` : undefined} autoComplete="email" inputMode="email" />
                      {errors.email && <p className="svc-quote__error" id={`err-email-${slug}`}>{errors.email}</p>}
                    </div>
                    <div className={`svc-quote__field ${errors.phone ? 'svc-quote__field--error' : ''}`}>
                      <label htmlFor={`svc-phone-${slug}`}>Phone *</label>
                      <input type="tel" id={`svc-phone-${slug}`} name="phone" required value={formData.phone} onChange={handleChange} onBlur={handleBlur} placeholder="+91 XXXXX XXXXX" aria-required="true" aria-invalid={!!errors.phone} aria-describedby={errors.phone ? `err-phone-${slug}` : undefined} autoComplete="tel" inputMode="tel" />
                      {errors.phone && <p className="svc-quote__error" id={`err-phone-${slug}`}>{errors.phone}</p>}
                    </div>
                  </div>

                  <div className="svc-quote__row">
                    <div className={`svc-quote__field ${errors.origin ? 'svc-quote__field--error' : ''}`}>
                      <label htmlFor={`svc-origin-${slug}`}>Origin *</label>
                      <input type="text" id={`svc-origin-${slug}`} name="origin" required value={formData.origin} onChange={handleChange} onBlur={handleBlur} placeholder="City, Country" aria-required="true" aria-invalid={!!errors.origin} aria-describedby={errors.origin ? `err-origin-${slug}` : undefined} />
                      {errors.origin && <p className="svc-quote__error" id={`err-origin-${slug}`}>{errors.origin}</p>}
                    </div>
                    <div className={`svc-quote__field ${errors.destination ? 'svc-quote__field--error' : ''}`}>
                      <label htmlFor={`svc-dest-${slug}`}>Destination *</label>
                      <input type="text" id={`svc-dest-${slug}`} name="destination" required value={formData.destination} onChange={handleChange} onBlur={handleBlur} placeholder="City, Country" aria-required="true" aria-invalid={!!errors.destination} aria-describedby={errors.destination ? `err-dest-${slug}` : undefined} />
                      {errors.destination && <p className="svc-quote__error" id={`err-dest-${slug}`}>{errors.destination}</p>}
                    </div>
                  </div>

                  <div className={`svc-quote__field ${errors.commodity ? 'svc-quote__field--error' : ''}`}>
                    <label htmlFor={`svc-commodity-${slug}`}>Commodity / Cargo Details *</label>
                    <input type="text" id={`svc-commodity-${slug}`} name="commodity" required value={formData.commodity} onChange={handleChange} onBlur={handleBlur} placeholder="Brief description" aria-required="true" aria-invalid={!!errors.commodity} aria-describedby={errors.commodity ? `err-commodity-${slug}` : undefined} />
                    {errors.commodity && <p className="svc-quote__error" id={`err-commodity-${slug}`}>{errors.commodity}</p>}
                  </div>

                  <div className="svc-quote__row">
                    <div className="svc-quote__field">
                      <label htmlFor={`svc-date-${slug}`}>Preferred Date</label>
                      <input type="date" id={`svc-date-${slug}`} name="date" value={formData.date} onChange={handleChange} />
                    </div>
                    <div className="svc-quote__field">
                      <label htmlFor={`svc-weight-${slug}`}>Weight (kg)</label>
                      <input type="number" id={`svc-weight-${slug}`} name="weight" value={formData.weight} onChange={handleChange} placeholder="e.g., 500" inputMode="decimal" min="0" step="0.1" />
                    </div>
                  </div>

                  <div className="svc-quote__field">
                    <label htmlFor={`svc-message-${slug}`}>Message</label>
                    <textarea id={`svc-message-${slug}`} name="message" rows="4" value={formData.message} onChange={handleChange} placeholder="Special requirements"></textarea>
                  </div>

                  <button type="submit" className="btn btn-primary svc-quote__submit" disabled={submitting} aria-busy={submitting}>
                    {submitting ? 'SENDING…' : 'SUBMIT ENQUIRY'} {!submitting && <ArrowRight size={16} />}
                  </button>
                </form>
              )}
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
};

export default ServicePageTemplate;
